import React, { useMemo } from 'react';
import moment from 'moment';
import { Badge, Table } from 'react-bootstrap';
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import { formatMonthBitmask, upcaseFirst } from '../../utils/formatBonus';
import { round } from '../../utils/formatNum';
export interface UserBonus {
  id: string | number;
  type: string;
  periodType?: string;
  year?: string | number;
  monthBitmask?: number;
  manualBonusId?: string | number;
  amount: number;
  submissionDate?: string;
  description?: string;
  quarter?: string | number;
  month?: string | number;
  isManagedUser?: boolean;
  userId?: string;
  user?: any;
}

// Map for badge colors by periodType
const periodTypeBadgeColor: Record<string, string> = {
  month: 'primary',
  quarter: 'success',
  halfyear: 'warning',
  year: 'info',
};

const typeBadgeColor: Record<string, string> = {
  Manual: 'success',
  'Health & Dev': 'info',
  Sales: 'warning',
};

const typeBadgeLabel: Record<string, string> = {
  Manual: 'Manual',
  'Health & Dev': 'H&D',
  Sales: 'GV',
};

interface UserBonusesTableProps {
  bonuses: UserBonus[];
  showTotalRow?: boolean;
}

const formatPeriodDetails = (bonus: any) => {
  if (bonus.type === 'Manual') {
    const badgeColor = periodTypeBadgeColor[bonus.periodType] || 'secondary';
    const periodLabel = formatMonthBitmask(Number(bonus.monthBitmask), bonus.periodType);
    let periodStr: any = `${bonus.year}-${periodLabel}`;
    if (bonus.detailUrl) {
      periodStr = (
        <a href={bonus.detailUrl} rel="noopener noreferrer" className="text-bold">
          {periodStr}
        </a>
      );
    }

    return (
      <td>
        <Badge bg={badgeColor} style={{ minWidth: 60, textAlign: 'center', marginRight: 8 }}>
          {upcaseFirst(bonus.periodType)}
        </Badge>
        {periodStr}
      </td>
    );
  }
  if (bonus.type === 'Health & Dev') {
    let periodStr: any = '-';
    if (bonus.year !== '-' && bonus.month !== '-') {
      periodStr = `${bonus.year}-${String(bonus.month).padStart(2, '0')}`;
      if (bonus.detailUrl) {
        periodStr = (
          <a href={bonus.detailUrl} rel="noopener noreferrer" className="text-bold">
            {periodStr}
          </a>
        );
      }
    }
    let quarterBadge = null;
    if (bonus.quarter && bonus.quarter !== '-') {
      quarterBadge = (
        <Badge bg="success" style={{ minWidth: 60, textAlign: 'center', marginRight: 8 }}>
          {`Q${bonus.quarter}`}
        </Badge>
      );
    }
    return (
      <td>
        {quarterBadge}
        {periodStr}
      </td>
    );
  }
  if (bonus.type === 'Sales') {
    let periodStr: any = '-';
    if (bonus.year !== '-' && bonus.quarter !== '-') {
      periodStr = `${[bonus.year, bonus.quarter].filter(Boolean).join('-')}`;
      if (bonus.detailUrl) {
        periodStr = (
          <a href={bonus.detailUrl} rel="noopener noreferrer" className="text-bold">
            {periodStr}
          </a>
        );
      }
    }
    let quarterBadge = null;
    if (bonus.periodType && bonus.periodType !== '-') {
      quarterBadge = (
        <Badge bg="success" style={{ minWidth: 60, textAlign: 'center', marginRight: 8 }}>
          {`${upcaseFirst(bonus.periodType)}`}
        </Badge>
      );
    }
    return (
      <td>
        {quarterBadge}
        {periodStr}
      </td>
    );
  }
  return <td>-</td>;
};

const UserBonusesTable: React.FC<UserBonusesTableProps> = ({ bonuses, showTotalRow = false }) => {
  const hasManagedUsersBonuses = useMemo(() => bonuses.some((b) => b.isManagedUser), [bonuses]);
  const columns = useMemo(
    () =>
      [
        hasManagedUsersBonuses && {
          Header: 'User',
          id: 'user',
          accessor: 'user',
          Cell: ({ data, row }: any) => {
            const user = data[row.id]?.user;
            return <td>{user ? [user.firstName, user.lastName].filter(Boolean).join(' ') : '-'}</td>;
          },
        },
        {
          Header: 'Type',
          id: 'type',
          accessor: 'type',
          Cell: ({ data, row }: any) => {
            const type = data[row.id]?.type;
            const badgeColor = typeBadgeColor[type] || 'secondary';
            return (
              <td>
                <Badge bg={badgeColor} style={{ minWidth: 60, textAlign: 'center' }}>
                  {typeBadgeLabel[type] || type}
                </Badge>
              </td>
            );
          },
        },
        {
          Header: 'Period',
          id: 'period',
          accessor: 'period',
          Cell: ({ data, row }: any) => {
            const bonus = data[row.id];
            return formatPeriodDetails(bonus);
          },
        },
        {
          Header: 'Amount',
          id: 'amount',
          accessor: 'amount',
          Cell: ({ data, row }: any) => <td>${round(data[row.id]?.amount, 2)}</td>,
        },
        {
          Header: 'Submitted',
          id: 'submissionDate',
          accessor: 'submissionDate',
          Cell: ({ data, row }: any) => {
            const date = data[row.id]?.submissionDate;
            return <td>{date ? moment(date).format('MMM D, YYYY') : '-'}</td>;
          },
        },
        {
          Header: 'Description',
          id: 'description',
          accessor: 'description',
          Cell: ({ data, row }: any) => <td>{data[row.id]?.description}</td>,
        },
        {
          Header: 'Status',
          id: 'status',
          accessor: 'status',
          Cell: ({ data, row }: any) => null,
        },
      ].filter(Boolean) as any[],
    [],
  );

  const renderBonusesTable = (bonuses) => (
    <Table striped bordered className="table-sm mb-0">
      <thead>
        <tr>
          {columns.map((col, idx) => (
            <th key={idx}>{col.Header}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {bonuses.map((bonus, idx) => (
          <tr key={idx}>
            {columns.map((col, cidx) => (
              <React.Fragment key={cidx}>{col.Cell({ data: bonuses, row: { id: idx } })}</React.Fragment>
            ))}
          </tr>
        ))}
        {showTotalRow && bonuses.length > 0 && (
          <tr>
            <td className="fw-bold" colSpan={hasManagedUsersBonuses ? 2 : 1}>
              Total Bonus
            </td>
            <td>{/* period range can be added if needed */}</td>
            <td className="fw-bold text-success">
              $
              {bonuses
                .reduce((sum, b) => {
                  return sum + (Number(b.amount) || 0);
                  // if (!b.isManagedUser) {
                  // }
                  // return sum;
                }, 0)
                .toLocaleString()}
            </td>
            {columns.length > 3 && Array.from({ length: columns.length - 3 }).map((_, i) => <td key={i}></td>)}
          </tr>
        )}
      </tbody>
    </Table>
  );

  const userBonusTableEle = renderBonusesTable(bonuses.filter((b) => !b.isManagedUser));
  if (hasManagedUsersBonuses) {
    const teamBonusTableEle = renderBonusesTable(bonuses.filter((b) => b.isManagedUser));
    return (
      <Tabs defaultActiveKey="my_bonuses" className="mb-3">
        <Tab eventKey="my_bonuses" title="My Bonuses">
          {userBonusTableEle}
        </Tab>
        <Tab eventKey="team_bonuses" title="Team Bonuses">
          {teamBonusTableEle}
        </Tab>
      </Tabs>
    );
  }
  return userBonusTableEle;
};

export default UserBonusesTable;
